import {StyleSheet, type CSSProperties} from "aphrodite";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {domainSlugMap, type DomainSlug} from "@khan/content-types";
import {
    borderRadius,
    contentWidth,
    mediaQueries,
    pageWidth,
    standardTransition,
} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import {
    moduleHorizontalPadding,
    moduleHorizontalPaddingSmall,
    moduleLayout,
} from "#modules-package/styles.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

export const mixins = {
    padding: (top = 0, right = 0, bottom = 0, left = 0): any => {
        return {
            paddingTop: top,
            paddingRight: right,
            paddingBottom: bottom,
            paddingLeft: left,
        };
    },

    position: (
        type: string,
        top: number,
        right: number,
        bottom: number,
        left: number,
    ): any => {
        return {
            position: type,
            top,
            right,
            bottom,
            left,
        };
    },
} as const;

export const overflowEllipsis = {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
} as const;

export const grayCallout = {
    // Used for mission callouts and resume learning placeholders on the
    // logged-in homepage
    background: color.offWhite,
    border: `1px solid ${color.offBlack8}`,
    borderRadius,
    transition: `borderColor ${standardTransition}`,

    ":focus": {
        border: `1px solid ${color.blue}`,
        textDecoration: "none",
    },
    ":hover": {
        border: `1px solid ${color.blue}`,
        textDecoration: "none",
    },
} as const;

export const thumbnailSizes = {
    xxxlarge: {
        height: 405,
        width: 720,
    },
    xxlarge: {
        height: 252,
        width: 448,
    },
    xlarge: {
        height: 207,
        width: 368,
    },
    large: {
        height: 117,
        width: 208,
    },
    medium: {
        height: 76,
        width: 136,
    },
    small: {
        height: 54,
        width: 96,
    },
} as const;

// TODO(emily): Replace all usages of these flex mixins with the plain
// attributes used inline now that aphrodite does browser prefixing.
export const displayFlex = {
    display: "flex",
} as const;

export const justifyContent = (val: string): CSSProperties => {
    return {
        justifyContent: val,
    };
};

export const alignItems = (val: string): CSSProperties => {
    return {
        alignItems: val,
    };
};

export const flexDirection = (val: string): CSSProperties => {
    return {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'FlexDirection | undefined'.
        flexDirection: val,
    };
};

export const flexWrap = (val: string): CSSProperties => {
    return {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'FlexWrap | undefined'.
        flexWrap: val,
    };
};

// This is used dynamically (i.e. in real inline styles, not aphrodite), so
// can't be replaced with plain `flex: val` inline.
export const flex = (val: string): CSSProperties => {
    return {
        flex: val,
    };
};

export const srOnly = {
    position: "absolute",
    left: "-10000px",
    top: "auto",
    width: 1,
    height: 1,
    overflow: "hidden",
} as const;

export const fixBorderRadiusWithOverflow: CSSProperties = {
    // Hack to work around Blink bug (see http://stackoverflow.com/questions/5736503/how-to-make-css3-rounded-corners-hide-overflow-in-chrome-opera)
    // Also, as a bonus, sorry for the line breaks; blame ESLint
    WebkitMaskImage:
        "url(data:image/png;base64," +
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2Fy" +
        "ZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJ" +
        "AAAAAElFTkSuQmCC)",
};

export const alignCenter: CSSProperties = {
    ...moduleLayout.defaultAlignment,
    boxSizing: "border-box",
};

export const pageHeaderPadding: CSSProperties = {
    paddingLeft: moduleHorizontalPadding,
    paddingRight: moduleHorizontalPadding,

    [mediaQueries.smOrSmaller]: {
        paddingLeft: moduleHorizontalPaddingSmall,
        paddingRight: moduleHorizontalPaddingSmall,
    },
};

export const pageHeader: CSSProperties = {
    ...alignCenter,
    ...pageHeaderPadding,
    paddingTop: 22,
    paddingBottom: 22,
    alignItems: "center",
    color: color.white,
    display: "flex",
    justifyContent: "space-between",
    minHeight: 94,
    textAlign: "left",

    [mediaQueries.mdOrLarger]: {
        paddingTop: 32,
        paddingBottom: 32,
    },

    // Disable subpixel antialiasing for OSX (improves light on dark text)
    MozOsxFontSmoothing: "grayscale",
    WebkitFontSmoothing: "antialiased",
};

export const pageHeaderTitle: CSSProperties = {
    color: color.white,
    marginBottom: 0,

    ...deprecatedTypography.conceptHeadingMobile,

    [mediaQueries.mdOrLarger]: {
        ...deprecatedTypography.conceptHeadingDesktop,
    },
};

export const contentCenterBox: CSSProperties = {
    ...pageHeaderPadding,
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: contentWidth,
};

export const contentPadding: CSSProperties = {
    ...contentCenterBox,
    maxWidth: contentWidth + moduleHorizontalPadding * 2,
    [mediaQueries.smOrSmaller]: {
        maxWidth: contentWidth + moduleHorizontalPaddingSmall * 2,
    },
};

export const navbar = {
    bar: {
        width: "100%",
        maxWidth: pageWidth,
        height: 48,
        position: "relative",
        left: -16,
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "flex-start",
        alignItems: "center",
        marginLeft: "auto",
        marginRight: "auto",
    },
    tab: {
        ...deprecatedTypography.bodySmallBold,
        display: "inline-flex",
        height: 48,
        boxSizing: "border-box",
        alignItems: "center",
        justifyContent: "center",
        textDecoration: "none",
        cursor: "pointer",
        boxShadow: `inset 0 -5px`,
        marginLeft: 16,
        marginRight: 16,
        marginTop: 0,
        marginBottom: 0,

        ":hover": {
            textDecoration: "none",
        },
    },
    inactiveTab: {
        ":hover": {
            boxShadow: `inset 0 -5px ${color.offBlack8}`,
            transition: "box-shadow 75ms ease-in-out",
        },
        boxShadow: `inset 0 0 transparent`,
        transition: "box-shadow 150ms ease-in-out",
    },
    activeTab: {
        // This 0% opacity outline is imperceptible under normal conditions, but it
        // appears as a normal full black outline on Windows high contrast mode so
        // users can see which tab is currently selected.
        outline: "1px solid rgba(0,0,0,0)",

        ":focus": {
            outline: "auto",
        },
    },
} as const;

// Returns the color theme of the assets on the topic progress page, determined
// by the domain
export const blueDomains = [
    "math",
    "computing",
    "college-careers-more",
    "test-prep",
] as const;
export const studyGuideTheme = (domain: string): string =>
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"test-prep" | "math" | "computing" | "college-careers-more"'.
    blueDomains.includes(domain) ? "blue" : "red";

// Styles for when rendering a subject page card on a small screen.
export const smallScreenCard = {
    borderRadius: 0,
    borderLeft: "none",
    borderRight: "none",
    width: "100%",
    margin: "0px 0px 16px 0px",
} as const;

// Styles for kMAP page/modal headers when the content is identified as "on-task"
export const ON_TASK_BANNER_COLOR = "#F2B88D";

export const onTaskHeaderBg = {
    backgroundColor: ON_TASK_BANNER_COLOR,
    backgroundImage: `${cssUrl("/images/mastery/on-task-banner-ma.png")}`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "top left",
    backgroundSize: "cover",
} as const;

// Domain colors by slug
// Note: Keep in sync with stylesheets/shared-package/variables.less
//       which holds the same values stored in less variables

export const domainToColorsMap = {
    math: {
        domain1: "#11accd",
        domain2: "#63d9ea",
        domain3: "#027d97",
        domain4: "#085566",
    },
    "partner-content": {
        domain1: "#01a995",
        domain2: "#01d1c1",
        domain3: "#208170",
        domain4: "#144f44",
    },
    computing: {
        domain1: "#1fab54",
        domain2: "#74cf70",
        domain3: "#0d923f",
        domain4: "#085e29",
    },
    "economics-finance-domain": {
        domain1: "#e07d10",
        domain2: "#ffbe26",
        domain3: "#a75a05",
        domain4: "#953c02",
    },
    humanities: {
        domain1: "#e84d39",
        domain2: "#ff8482",
        domain3: "#be2612",
        domain4: "#8c1c0d",
    },
    science: {
        domain1: "#ca337c",
        domain2: "#ff92c6",
        domain3: "#9e034e",
        domain4: "#6b0235",
    },
    "test-prep": {
        domain1: "#1865F2",
        domain2: "#669ff4",
        domain3: "#0449c9",
        domain4: "#013493",
    },
    "college-careers-more": {
        domain1: "#01a995",
        domain2: "#01d1c1",
        domain3: "#208170",
        domain4: "#144f44",
    },
    default: {
        domain1: "#1865F2",
        domain2: "#66afe9",
        domain3: "#1152b0",
        domain4: "#0d3d82",
    },
} as const;

// Use globalStyles.domainColors(domain) instead of
// globalStyles.colors[domain], since we handle new domains correctly here.
export function domainColors(domain?: string | null): any {
    return domainToColorsMap[getValidDomainSlug(domain)];
}

export function getValidDomainSlug(domain?: string | null): DomainSlug {
    if (domain && domainSlugMap[domain]) {
        return domainSlugMap[domain];
    }
    return "default";
}

// Generate Aphrodite objects for this template in every domain color
type cssDomainFunc = (domain: DomainSlug) => CSSProperties;
export const makeDomainStylesForTemplate = (
    func: cssDomainFunc,
): {
    ["college-careers-more"]: CSSProperties;
    computing: CSSProperties;
    default: CSSProperties;
    ["economics-finance-domain"]: CSSProperties;
    humanities: CSSProperties;
    math: CSSProperties;
    ["partner-content"]: CSSProperties;
    science: CSSProperties;
    ["test-prep"]: CSSProperties;
} =>
    StyleSheet.create(
        Object.keys(domainToColorsMap).reduce<Record<string, any>>(
            (result, domain) => {
                result[domain] = func(domain as DomainSlug);
                return result;
            },
            {},
        ),
    ) as any;

// e.g. export const domainLink = sharedStyles.links[domain]
export const links: Record<string, any> = makeDomainStylesForTemplate(
    (domain) => ({
        color: domainColors(domain).domain3,

        ":visited": {
            color: domainColors(domain).domain4,
        },
    }),
);
